import { useState } from "react";
import FormField from "./FormField";

const initialForm = {
  email: "",
  role: "Editor",
};

export default function InviteUserModal({
  show,
  onClose,
  onInvite,
  existingEmails = [],
}) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [apiError, setApiError] = useState("");

  if (!show) return null;

  const updateField = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));

    setErrors((current) => ({
      ...current,
      [name]: "",
    }));

    setApiError("");
  };

  const validate = () => {
    const nextErrors = {};
    const email = form.email.trim().toLowerCase();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      nextErrors.email = "Email address is required.";
    } else if (!emailPattern.test(email)) {
      nextErrors.email = "Enter a valid email address.";
    } else if (
      existingEmails.some(
        (existingEmail) => existingEmail.toLowerCase() === email
      )
    ) {
      nextErrors.email = "This user is already in the team.";
    }

    if (!form.role) {
      nextErrors.role = "Please select a role.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setApiError("");

    if (!validate()) return;

    try {
      setLoading(true);

      const invited = await onInvite({
        email: form.email.trim().toLowerCase(),
        role: form.role,
      });

      // Demo mode has no email provider, so the invite code is shown here for the admin to share.
      setInviteCode(invited?.inviteCode || "");
      setSuccess(true);
    } catch (error) {
      setApiError(
        error?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;

    setForm(initialForm);
    setErrors({});
    setApiError("");
    setSuccess(false);
    setInviteCode("");
    onClose();
  };

  return (
    <div className="invite-modal-backdrop" role="presentation">
      <div
        className="invite-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inviteUserTitle"
      >
        <div className="d-flex justify-content-between align-items-start border-bottom p-4">
          <div>
            <h5 className="mb-1" id="inviteUserTitle">
              Invite user
            </h5>
            <p className="text-muted small mb-0">
              Add a team member to your ChatBizz workspace.
            </p>
          </div>

          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
            disabled={loading}
          />
        </div>

        {success ? (
          <div className="p-4">
            <div className="invite-success-icon mb-3">
              <i className="bi bi-check-lg" />
            </div>

            <h5>Invitation sent</h5>
            <p className="text-muted mb-4">
              An invitation has been created for{" "}
              <strong>{form.email}</strong> with the{" "}
              <strong>{form.role}</strong> role.
            </p>

            {inviteCode && (
              <div className="alert alert-info text-center" data-testid="invite-code">
                <div className="small mb-1">Invite code (share it with the invitee — they enter it when signing up)</div>
                <strong style={{ fontSize: 24, letterSpacing: 4 }}>{inviteCode}</strong>
              </div>
            )}

            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleClose}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4">
              {apiError && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-circle me-2" />
                  {apiError}
                </div>
              )}

              <FormField
                label="Email address"
                htmlFor="inviteEmail"
                error={errors.email}
                helpText="The invitation will be sent to this email address."
              >
                <input
                  id="inviteEmail"
                  name="email"
                  type="email"
                  className={`form-control ${
                    errors.email ? "is-invalid" : ""
                  }`}
                  placeholder="name@company.com"
                  value={form.email}
                  onChange={updateField}
                  disabled={loading}
                  autoComplete="email"
                />
              </FormField>

              <FormField
                label="Role"
                htmlFor="inviteRole"
                error={errors.role}
                helpText="The role determines what this user can access."
              >
                <select
                  id="inviteRole"
                  name="role"
                  className={`form-select ${
                    errors.role ? "is-invalid" : ""
                  }`}
                  value={form.role}
                  onChange={updateField}
                  disabled={loading}
                >
                  <option value="Admin">Admin</option>
                  <option value="Editor">Editor</option>
                  <option value="Viewer">Viewer</option>
                </select>
              </FormField>

              <div className="invite-role-note">
                <i className="bi bi-info-circle me-2" />
                The invited user will appear in your team list with an
                “Invited” status.
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 border-top p-3">
              <button
                type="button"
                className="btn btn-light border"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      aria-hidden="true"
                    />
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send me-2" />
                    Send invitation
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}